import { INotificationsService, IRpcService, notifyOnError } from '~/rage';
import { IObservable, Subject } from '~/rage/utils';
import { IReport, IReportMessage, ReportState } from '~/shared';
import { BackendService } from '../BackendService';
import { IPlayerService } from '../PlayerService';
import {
	IActiveReportMessagesState,
	IReportService,
} from './reportServiceTypes';

const initialMessagesState: IActiveReportMessagesState = {
	messages: [],
	loading: false,
};

export class ReportService implements IReportService {
	private readonly reports = new Subject<IReport[]>([]);
	private readonly activeReportMessages =
		new Subject<IActiveReportMessagesState>(initialMessagesState);

	private currentReportId: string | null = null;
	private readonly messagesCache = new Map<string, IReportMessage[]>();

	constructor(
		private readonly backend: BackendService,
		private readonly rpc: IRpcService,
		player: IPlayerService,
		private readonly notifications: INotificationsService,
	) {
		this.backend.addEventHandler(
			'reports:newReport',
			this.handleNewReport.bind(this),
		);
		this.rpc.register('reports:newReport', this.handleNewReport.bind(this));

		this.backend.addEventHandler(
			'reports:statusUpdated',
			this.handleStatusUpdated.bind(this),
		);
		this.rpc.register(
			'reports:statusUpdated',
			this.handleStatusUpdated.bind(this),
		);

		this.backend.addEventHandler(
			'reports:messageAdded',
			this.handleMessageAdded.bind(this),
		);
		this.rpc.register(
			'reports:messageAdded',
			this.handleMessageAdded.bind(this),
		);

		this.backend.addEventHandler(
			'reports:lastMessageUpdated',
			this.handleLastMessageUpdated.bind(this),
		);

		this.backend.addEventHandler(
			'reports:playerOnlineStatusUpdated',
			this.handlePlayerOnlineStatusUpdated.bind(this),
		);

		this.backend.addEventHandler(
			'reports:staffJoined',
			this.handleStaffJoined.bind(this),
		);
		this.rpc.register('reports:staffJoined', this.handleStaffJoined.bind(this));

		this.backend.addEventHandler(
			'reports:staffLeft',
			this.handleStaffLeft.bind(this),
		);
		this.rpc.register('reports:staffLeft', this.handleStaffLeft.bind(this));

		player.waitForCharacterSelect().then(() => {
			this.loadReports().catch(notifications.showException);
		});
	}

	private handleStatusUpdated(data: {
		reportId: string;
		state: ReportState;
		isHelpNeeded: boolean;
	}): void {
		this.handleStatusUpdate(data.reportId, data.state, data.isHelpNeeded);
	}

	private handleMessageAdded(data: {
		reportId: string;
		message: IReportMessage;
	}) {
		this.handleNewMessage(data.reportId, data.message);
		this.handleLastMessageUpdate(
			data.reportId,
			data.message.message,
			data.message.isStaffMessage,
		);
		if (data.message.isStaffMessage && data.message.playerName) {
			this.handleParticipantAdded(data.reportId, data.message.playerName);
		}
	}

	@notifyOnError
	private async loadReports(): Promise<void> {
		const reports = await this.backend.invoke<IReport[]>('reports:getReports');

		this.reports.next(reports);
	}

	public getReports(): IObservable<IReport[]> {
		return this.reports;
	}

	public setActiveReportId(reportId: string | null): void {
		this.currentReportId = reportId;

		if (reportId === null) {
			this.activeReportMessages.next(initialMessagesState);
			return;
		}

		this.activeReportMessages.next({ messages: [], loading: true });

		const cached = this.messagesCache.get(reportId);
		if (cached) {
			this.activeReportMessages.next({
				messages: cached,
				loading: false,
			});

			return;
		}

		this.backend
			.invoke<IReportMessage[]>('reports:getMessages', { reportId })
			.then((messages) => {
				this.messagesCache.set(reportId, messages);
				if (this.currentReportId === reportId) {
					this.activeReportMessages.next({
						messages,
						loading: false,
					});
				}
			})
			.catch((err) => {
				this.notifications.showException(err);

				if (this.currentReportId === reportId) {
					this.activeReportMessages.next({
						messages: [],
						loading: false,
					});
				}
			});
	}

	public getActiveReportMessages(): IObservable<IActiveReportMessagesState> {
		return this.activeReportMessages;
	}

	public preloadMessages(reportId: string, messages: IReportMessage[]): void {
		this.messagesCache.set(reportId, messages);
	}

	@notifyOnError
	public async sendMessage(reportId: string, message: string): Promise<void> {
		await this.backend.invoke('reports:sendMessage', {
			reportId,
			message,
		});
	}

	@notifyOnError
	public async createReport(
		message: string,
	): Promise<{ report: IReport; messages: IReportMessage[] }> {
		return this.backend.invoke('reports:createReport', { message });
	}

	@notifyOnError
	public async joinReport(reportId: string): Promise<void> {
		await this.backend.invoke('reports:joinReport', { reportId });
	}

	@notifyOnError
	public async setHelpNeeded(
		reportId: string,
		isHelpNeeded: boolean,
	): Promise<void> {
		await this.backend.invoke('reports:setHelpNeeded', {
			reportId,
			isHelpNeeded,
		});
	}

	@notifyOnError
	public async setReportClosed(reportId: string): Promise<void> {
		await this.backend.invoke('reports:setClosed', {
			reportId,
		});
	}

	@notifyOnError
	public async setReportCompleted(reportId: string): Promise<void> {
		await this.backend.invoke('reports:setReportCompleted', {
			reportId,
		});
	}

	@notifyOnError
	public async setReportActive(reportId: string): Promise<void> {
		await this.backend.invoke('reports:setReportActive', {
			reportId,
		});
	}

	@notifyOnError
	public async openTicket(reportId: string): Promise<void> {
		await this.setReportActive(reportId);
	}

	@notifyOnError
	public async closeTicket(reportId: string): Promise<void> {
		await this.setReportCompleted(reportId);
	}

	@notifyOnError
	public async leaveReport(reportId: string): Promise<void> {
		await this.backend.invoke('reports:leaveReport', { reportId });
	}

	@notifyOnError
	public async spectate(characterId: string): Promise<void> {
		await this.rpc.invokeServer('reports:spectate', { characterId });
	}

	@notifyOnError
	public async tpToPlayer(characterId: string): Promise<void> {
		await this.rpc.invokeServer('reports:tpToPlayer', { characterId });
	}

	@notifyOnError
	public async tpPlayerToMe(characterId: string): Promise<void> {
		await this.rpc.invokeServer('reports:tpPlayerToMe', { characterId });
	}

	private handleNewMessage(reportId: string, message: IReportMessage): void {
		const cached = this.messagesCache.get(reportId) ?? [];
		const exists = cached.some((m) => m.id === message.id);

		if (exists) {
			return;
		}

		const next = [...cached, message];
		this.messagesCache.set(reportId, next);

		if (this.currentReportId === reportId) {
			this.activeReportMessages.next({
				messages: next,
				loading: false,
			});
		}
	}

	private handleNewReport(newReport: IReport): void {
		const currentReports = this.reports.getValue();
		const exists = currentReports.some((r) => r.id === newReport.id);

		if (!exists) {
			this.reports.next([...currentReports, newReport]);
		}
	}

	private handleStatusUpdate(
		reportId: string,
		state: ReportState,
		isHelpNeeded: boolean,
	): void {
		const currentReports = this.reports.getValue();
		const updatedReports = currentReports.map((r) =>
			r.id === reportId ? { ...r, state, isHelpNeeded } : r,
		);

		this.reports.next(updatedReports);
	}

	private handleLastMessageUpdate(
		reportId: string,
		message: string,
		lastMessageIsFromStaff?: boolean,
	): void {
		const currentReports = this.reports.getValue();
		const updatedReports = currentReports.map((r) =>
			r.id === reportId
				? { ...r, lastMessage: message, lastMessageIsFromStaff }
				: r,
		);

		this.reports.next(updatedReports);
	}

	private handleLastMessageUpdated(data: {
		reportId: string;
		lastMessage: string;
		lastMessageIsFromStaff?: boolean;
	}): void {
		this.handleLastMessageUpdate(
			data.reportId,
			data.lastMessage,
			data.lastMessageIsFromStaff,
		);
	}

	private handlePlayerOnlineStatusUpdated(data: {
		characterId: string;
		playerOnline: boolean;
	}): void {
		const currentReports = this.reports.getValue();
		const updatedReports = currentReports.map((r) =>
			r.characterId === data.characterId
				? { ...r, playerOnline: data.playerOnline }
				: r,
		);

		this.reports.next(updatedReports);
	}

	private handleStaffJoined(data: {
		reportId: string;
		staffNickname: string;
	}): void {
		const currentReports = this.reports.getValue();
		const updatedReports = currentReports.map((r) => {
			if (r.id === data.reportId) {
				const viewingStaff = r.viewingStaff || [];
				if (!viewingStaff.includes(data.staffNickname)) {
					return {
						...r,
						viewingStaff: [...viewingStaff, data.staffNickname],
					};
				}
			}
			return r;
		});

		this.reports.next(updatedReports);
	}

	private handleStaffLeft(data: {
		reportId: string;
		staffNickname: string;
	}): void {
		const currentReports = this.reports.getValue();
		const updatedReports = currentReports.map((r) => {
			if (r.id === data.reportId) {
				const viewingStaff = (r.viewingStaff || []).filter(
					(n) => n !== data.staffNickname,
				);
				return {
					...r,
					viewingStaff,
				};
			}
			return r;
		});

		this.reports.next(updatedReports);
	}

	private handleParticipantAdded(
		reportId: string,
		staffNickname: string,
	): void {
		const currentReports = this.reports.getValue();
		const updatedReports = currentReports.map((r) => {
			if (r.id === reportId) {
				const participants = r.participants || [];
				if (!participants.includes(staffNickname)) {
					return {
						...r,
						participants: [...participants, staffNickname],
					};
				}
			}
			return r;
		});

		this.reports.next(updatedReports);
	}
}
